// Module 12 — composition root (injectable). Wires the frozen modules into the running app WITHOUT
// reaching into any internals: worker(08) ⇄ state(09) ⇄ viz(10)/dashboards(11). Data path:
// worker snapshot → viz.applySnapshot (which pushes into the store) → dashboards (subscribed to the
// store) reflect it. Command path: UI → store.dispatchCommand → the injected Transport (the worker).
// The app also bridges store `layersVisible` → viz.setLayerVisible (the worker emits all layers; per-
// layer visibility is a render concern) so a layer toggle is disable-safe (R7). The real adapters/worker
// are bound in index.ts; tests inject fakes here. No new interface; compose-via-frozen-APIs only.
import type { Store, UiState, Transport } from "../state/index";
import type { Layer, Snapshot } from "../domain/index";
import type { CesiumAdapter, VizModel } from "../viz/index";
import type { SimWorkerApi } from "../worker/index";
import type { Governor } from "./governor";

const LAYERS: readonly Layer[] = ["energy", "information", "finance"];

export interface ComposeDeps {
  worker: SimWorkerApi;
  store: Store<UiState>;
  setTransport: (t: Transport | null) => void;
  viz: CesiumAdapter;
  mountDashboards: (root: HTMLElement) => () => void;
  model: VizModel;
  cesiumRoot: HTMLElement;
  dashRoot: HTMLElement;
  modelUrl: string;
  governor?: Governor;
  autostart?: boolean; // default true — begin ticking after boot
  // render-coalescing seam (defaults to the DOM rAF). The worker posts snapshots on its own clock; we
  // render at most ONE per animation frame from the freshest snapshot, so a fast/heavy sim can never
  // flood the main thread. Injectable so the coalescing is unit-testable in node (no rAF there).
  scheduleFrame?: (cb: () => void) => number;
  cancelFrame?: (id: number) => void;
}

export interface AppHandle {
  dispose(): void;
}

export async function composeApp(deps: ComposeDeps): Promise<AppHandle> {
  const { worker, store, setTransport, viz, mountDashboards, model, governor } = deps;

  // command path: UI → store → worker (SimWorkerApi is a structural superset of Transport).
  setTransport(worker);

  // boot sequence — order asserted by the composition test: init → subscribe → mount → mountDashboards.
  const initPromise = worker.init(deps.modelUrl);

  // Render coalescing (the freeze fix). The worker free-runs its clock and posts a snapshot every tick;
  // rendering every one synchronously lets a fast/heavy sim outpace the main thread, the message queue
  // grows without bound, and the tab hangs. Instead we stash only the NEWEST snapshot and apply it at
  // most once per animation frame — intermediate snapshots are coalesced away for RENDER (the store
  // still ends up latest-coherent, since each applied snapshot carries the full current state). This
  // caps main-thread render work at the display rate regardless of sim speed.
  const scheduleFrame = deps.scheduleFrame ?? ((cb): number => requestAnimationFrame(cb));
  const cancelFrame = deps.cancelFrame ?? ((id): void => cancelAnimationFrame(id));
  let pendingSnap: Snapshot | null = null;
  let frameScheduled = false;
  let frameId = 0;
  const pumpFrame = (): void => {
    frameScheduled = false;
    const s = pendingSnap;
    pendingSnap = null;
    if (s) viz.applySnapshot(s); // → store → dashboards; once per frame, newest snapshot only
  };
  worker.subscribe((s) => {
    pendingSnap = s; // keep only the freshest; drop the ones we'd never get to paint
    if (!frameScheduled) { frameScheduled = true; frameId = scheduleFrame(pumpFrame); }
  });
  viz.mount(deps.cesiumRoot, model);
  const unmountDash = mountDashboards(deps.dashRoot);

  // disable-safe bridge (R7): mirror store layer visibility onto the render layer.
  let lastLayers = { ...store.getState().layersVisible };
  const unsubscribeBridge = store.subscribe((s) => {
    for (const layer of LAYERS) {
      if (s.layersVisible[layer] !== lastLayers[layer]) {
        viz.setLayerVisible(layer, s.layersVisible[layer]);
      }
    }
    lastLayers = { ...s.layersVisible };
  });

  await initPromise; // worker has built its SimModel and is ready
  governor?.start();
  if (deps.autostart !== false) store.getState().setRunning(true); // → transport.start (worker ticks)

  return {
    dispose(): void {
      governor?.stop();
      if (frameScheduled) { cancelFrame(frameId); frameScheduled = false; }
      pendingSnap = null;
      unsubscribeBridge();
      unmountDash();
      viz.dispose();
      store.getState().setRunning(false); // → transport.stop
      setTransport(null);
    },
  };
}
